import { type Scope, createContextScope } from '@radix-ui/react-context'
import React, { forwardRef } from 'react'
import { ModalBody } from './components/modal-body'
import { ModalClosableButton } from './components/modal-closable-button'
import { ModalDescription } from './components/modal-description'
import {
  ModalFirstButtonFooter,
  ModalFooter,
  ModalSecondButtonFooter,
} from './components/modal-footer'
import { ModalHeader } from './components/modal-header'
import { ModalIcon } from './components/modal-icon'
import { ModalTitle } from './components/modal-title'
import { modalVariants } from './modal.styles'
import type { ModalProps } from './modal.types'

type ModalContext = Pick<ModalProps, 'variant' | 'visible'>

const DISPLAY_NAME = 'Modal'

const ModalRoot = forwardRef<HTMLDivElement, ModalProps>(
  (props: ModalScopedProps<ModalProps>, ref) => {
    const { children, variant, visible, id, _scopeModal, ...rest } = props

    const modalClassName = modalVariants({
      variant,
    })

    const renderInternalHeaderIcon = React.Children.map(children, child => {
      if (!React.isValidElement(child)) return

      if (React.isValidElement(child) && child.type === ModalIcon) {
        return React.cloneElement(child)
      }
    })

    const renderInternalHeaderTitle = React.Children.map(children, child => {
      if (!React.isValidElement(child)) return

      if (React.isValidElement(child) && child.type === ModalTitle) {
        return React.cloneElement(child)
      }
    })

    const renderInternalHeaderClosableButton = React.Children.map(
      children,
      child => {
        if (!React.isValidElement(child)) return

        if (React.isValidElement(child) && child.type === ModalClosableButton) {
          return React.cloneElement(child)
        }
      }
    )

    const renderInternalDescription = React.Children.map(children, child => {
      if (!React.isValidElement(child)) return

      if (React.isValidElement(child) && child.type === ModalDescription) {
        return React.cloneElement(child)
      }
    })

    const renderInternalBody = React.Children.map(children, child => {
      if (!React.isValidElement(child)) return

      if (React.isValidElement(child) && child.type === ModalBody) {
        return React.cloneElement(child)
      }
    })

    const renderInternalFooter = React.Children.map(children, child => {
      if (!React.isValidElement(child)) return

      if (React.isValidElement(child) && child.type === ModalFooter) {
        return React.cloneElement(child)
      }
    })

    if (!visible) {
      return null
    }

    return (
      <ModalProvider scope={_scopeModal} variant={variant} visible={visible}>
        <div className="flex h-[100%] w-[100%] items-center justify-center overflow-hidden">
          <div
            style={{ backgroundColor: 'rgba(66, 66, 66, 0.32)' }}
            className="absolute top-0 right-0 bottom-0 left-0 h-[100vh] w-[100vw] overflow-hidden"
          />
          <div {...rest} ref={ref} className={modalClassName} data-testid={id}>
            <ModalHeader>
              {renderInternalHeaderIcon}
              {renderInternalHeaderTitle}
              <div className="ml-auto flex w-[80px] items-center justify-end bg-transparent">
                {renderInternalHeaderClosableButton}
              </div>
            </ModalHeader>
            <div
              data-testid="first-description-container"
              className="w-[100%] rounded-none border-[#e6e6e6] border-r-quarterpulse border-l-quarterpulse bg-[#FFF] pt-twopulse pr-sixpulse pb-twopulse pl-sixpulse"
            >
              {renderInternalDescription?.slice(0, 1)}
            </div>
            {renderInternalBody}
            <div
              data-testid="second-description-container"
              className="w-[100%] rounded-none border-[#e6e6e6] border-r-quarterpulse border-l-quarterpulse bg-[#FFF] pt-twopulse pr-sixpulse pb-twopulse pl-sixpulse"
            >
              {renderInternalDescription?.slice(1, 2)}
            </div>
            {renderInternalFooter}
          </div>
        </div>
      </ModalProvider>
    )
  }
)

ModalRoot.displayName = DISPLAY_NAME

/*
Scope Definition
*/

export type ModalScopedProps<P> = P & {
  _scopeModal?: Scope
}

const [createModalContext] = createContextScope(DISPLAY_NAME)

export const [ModalProvider, useModalContext]: readonly [
  ProviderType<ModalContext>,
  (consumerName: string, scope: Scope) => ModalContext,
] = createModalContext<ModalContext>(DISPLAY_NAME)

/*
Composition Export
*/

export const Modal = {
  Root: ModalRoot,
  HeaderIcon: ModalIcon,
  HeaderTitle: ModalTitle,
  HeaderClosableButton: ModalClosableButton,
  Description: ModalDescription,
  Body: ModalBody,
  Footer: ModalFooter,
  PrimaryButton: ModalFirstButtonFooter,
  SecondaryButton: ModalSecondButtonFooter,
}
